import { z } from 'zod';
import { Timestamp } from '@firebase/firestore';

// Zod schemas are the **single source of truth** for Firestore document
// shapes. TS types in src/types/index.ts are derived via `z.infer<>` so
// the two can never drift. Runtime validation happens in mapDoc/mapDocs
// when data crosses the Firestore trust boundary.

// ----- primitives -----

export const TimestampSchema = z.custom<Timestamp>(
  (val) => val instanceof Timestamp,
  'Expected Firestore Timestamp'
);

export const stakeholderTypeSchema = z.enum(['homeowner', 'business', 'municipality']);
export const postCategorySchema = z.enum(['progress', 'risk', 'news']);
export const checklistThreadAuthorRoleSchema = z.enum(['owner', 'agent', 'other']);

// Owner-set status on a "Notify my agent" send. Mirrors the three states
// the client asks the owner to pick from — see src/constants/evaluationStatus.ts
// for labels/blurbs (single source of truth for both).
export const evaluationStatusSchema = z.enum(['started', 'needs-input', 'complete']);

// User roles per client plan. Fire Scout is the default for every user
// (including anonymous "guest" sessions). Phase 1 only Fire Scout / Property
// Owner / Insurance Agent have actual capability — the rest are labels until
// Phase 2+ surfaces ship.
export const userRoleSchema = z.enum([
  'fire-scout',
  'property-owner',
  'insurance-agent',
  'insurance-company',
  'contractor',
  'evaluator',
  'fire-official',
  'community-leader',
  'educator',
]);

// Visibility scoping for posts. Owner defaults flow from
// structureClaims.defaultPostVisibility; individual posts may override.
// `insurance` means only the claim's `reportRecipients` + agents/insurers
// can read.
export const postVisibilitySchema = z.enum(['public', 'private', 'insurance']);

// ----- shared shapes -----

export const LocationSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
  address: z.string().optional(),
  geohash: z.string(),
  // Radius of the GPS fix in metres, as the device reported it. Absent on
  // every doc written before 2026-07-31 and on hand-placed pins, where there
  // is no accuracy to report. Nullable because the browser can hand back a
  // position with `coords.accuracy` unset.
  accuracyM: z.number().nullable().optional(),
});

export const GeoJSONPolygonSchema = z.object({
  type: z.literal('Polygon'),
  coordinates: z.array(z.array(z.array(z.number()))),
});

// ----- defense plans (community drawing, Sept 2026) -----
//
// One doc per plan, features inline (defensePlans/{planId}). A plan is well
// under Firestore's 1 MiB doc limit, so one atomic write, one snapshot, one
// read for the preview function covers it — no subcollection.

// Glenn's strategies (eight from his Sept 9 2026 call, more since);
// labels/colors/icons live in src/components/map/defenseStrategies.ts (the
// single source of truth for display, not this file).
export const defenseStrategySchema = z.enum([
  'fire-break',
  'metal-fence',
  'tree-thinning',
  'brush-clearing',
  'water-tank',
  'home-wetting',
  'sprinklers',
  'fireproof-paint',
  'house-hardening',
  'remove-fuel',
  'sprinkler-towers',
  'limb-trees',
  'fire-sensor',
  'grazing',
  'prescribed-fire',
  'defense-mesh',
  'upgrade-roofing',
  'upgrade-siding',
  'upgrade-vents',
  'upgrade-windows',
]);

export const defenseFeatureKindSchema = z.enum(['line', 'point', 'area']);

// Default 'shared' — flip to 'private' from the panel. No isAuthed() gate on
// the shared read branch in firestore.rules, so a /plan/<id> link works
// signed out, same reasoning as comments on a public post.
export const planVisibilitySchema = z.enum(['private', 'shared']);

// Rules can only enforce features.size() (a per-doc array length). The
// per-line vertex cap is enforced here and in the draw hook; the 1 MiB doc
// limit is the backstop, which is acceptable because it's always the
// owner's own doc.
export const MAX_FEATURES_PER_PLAN = 200;
export const MAX_VERTICES_PER_LINE = 500;

export const PlanBoundsSchema = z.object({
  n: z.number(),
  s: z.number(),
  e: z.number(),
  w: z.number(),
});

// A single drawn shape. coords is always { lat, lng }[] regardless of kind —
// a point is a length-1 array rather than a separate {lat,lng} shape, so the
// renderer branches on `kind` alone instead of on the shape of `coords`.
// Coords are maps, not [lat,lng] tuples: Firestore rejects an array value
// nested directly inside another array ("Property array contains an invalid
// nested entity"), and coords lives inside the plan doc's `features` array.
//
// createdAt must be set with Timestamp.now(), never serverTimestamp():
// Firestore rejects server-timestamp sentinels inside arrays, and every
// feature lives inside the plan doc's `features` array.
export const DefensePlanFeatureSchema = z
  .object({
    id: z.string(),
    strategy: defenseStrategySchema,
    kind: defenseFeatureKindSchema,
    coords: z.array(z.object({ lat: z.number(), lng: z.number() })),
    note: z.string().max(200).optional(),
    // A defense applied to a specific house rather than a spot on the
    // ground (Glenn's Sept 16 2026 ask: "I want to pick the houses").
    // Stored as kind 'point' with coords set to the structure's centroid —
    // the kind enum stays line|point|area on purpose so a build already
    // deployed still parses this feature; `structureId` is what makes it a
    // "home" feature rather than an ordinary drawn point. `address` is
    // captured at draw time (from the structure's own record) so the
    // feature list and map tooltip can show it without a lookup.
    structureId: z.string().max(200).optional(),
    address: z.string().max(200).optional(),
    // A feature belongs to at most one bundle (Sept 16 2026 — see
    // PlanBundleSchema above), by id. Absent means unbundled; the store's
    // bundle actions are the only writers, and clear it back to absent
    // (never '') when a feature leaves a bundle.
    bundleId: z.string().optional(),
    createdAt: TimestampSchema,
  })
  .refine(
    (feature) => {
      if (feature.kind === 'point') return feature.coords.length === 1;
      const min = feature.kind === 'area' ? 3 : 2;
      return feature.coords.length >= min && feature.coords.length <= MAX_VERTICES_PER_LINE;
    },
    {
      message:
        'A point feature needs exactly 1 coordinate; a line needs 2 to MAX_VERTICES_PER_LINE; ' +
        'an area needs 3 to MAX_VERTICES_PER_LINE.',
      path: ['coords'],
    }
  );

// A named group of a plan's features (Glenn's Sept 16 2026 ask: "bundle
// steps" — pick a set of homes/defenses, name the set, so one vendor bid and
// one responsibility assignment can cover all of them at once). Lives inline
// on the plan doc, the same reasoning DefensePlanFeatureSchema's own comment
// gives for features: one doc, one write, one read. A feature belongs to at
// most one bundle via its own optional `bundleId` below, not the other way
// around, so "is this feature bundled" is always a single field read.
//
// Oct 5 2026 (Glenn: "assign more than one person to a bundle and assign a
// leader"): a bundle can carry a `team` of responsible parties and one
// `leaderPartyId`. Both are optional and stay absent, never ''/undefined, when
// unset. `team` absent means "never set" (every bundle written before this
// shipped), and readers derive it from the assignments instead — see
// effectiveBundleTeam in utils/planBundles.ts. Plan-doc rules only cap the
// bundles list length, so no rules change is needed for these fields.
export const MAX_BUNDLE_TEAM = 20;
export const PlanBundleTeamMemberSchema = z.object({
  partyId: z.string(),
  description: z.string().max(200).optional(),
});
export const MAX_BUNDLE_INVITED_VENDORS = 20;
export const PlanBundleInvitedVendorSchema = z.object({
  vendorId: z.string(),
  repId: z.string().optional(),
  organization: z.string().min(1).max(200),
  repName: z.string().max(120).optional(),
  invitedAt: TimestampSchema.optional(),
});
export const PlanBundleSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(60),
  createdAt: TimestampSchema,
  team: z.array(PlanBundleTeamMemberSchema).max(MAX_BUNDLE_TEAM).optional(),
  leaderPartyId: z.string().optional(),
  // Open for vendor bids (Oct 2026): vendors with the public opportunity
  // link can say "I can help" with a price. bidsOpenedAt is when it was last
  // opened, so the Program panel can tell how long a bundle has waited.
  bidsOpen: z.boolean().optional(),
  bidsOpenedAt: TimestampSchema.optional(),
  // Redi bucks (Oct 2026). estimate is the owner's own cost guess in whole
  // dollars; it never reaches the opportunity page, so vendors aren't
  // anchored. workDoneAt is the owner marking the bundle's work done, which
  // is what lets a moderator release payment.
  estimate: z.number().int().min(0).max(10_000_000).optional(),
  workDoneAt: TimestampSchema.optional(),
  // Vendors the owner picked from the shared directory to invite (Nov
  // 2026). A shortlist, not the team: a vendor joins the team only by
  // bidding and being accepted, so a rep's email reaches a plan only when
  // the rep gives it. Names are copied so the plan renders without a
  // directory read; email and phone never are. invitedAt is the last time
  // the owner opened an email invite.
  invitedVendors: z.array(PlanBundleInvitedVendorSchema).max(MAX_BUNDLE_INVITED_VENDORS).optional(),
});

export const MAX_BUNDLES_PER_PLAN = 50;

export const DefensePlanSchema = z.object({
  id: z.string(),
  ownerId: z.string(),
  ownerName: z.string(),
  // useServiceAreasStore.getState().findContaining() of the first committed
  // vertex, else 'unassigned' — same fallback claims use. Backend-only, per
  // hard rule 6: never shown in UI copy, never used to block a user.
  areaId: z.string(),
  title: z.string().min(1).max(80),
  visibility: planVisibilitySchema.default('shared'),
  features: z.array(DefensePlanFeatureSchema).max(MAX_FEATURES_PER_PLAN),
  featureCount: z.number(),
  bounds: PlanBoundsSchema.nullable(),
  // Optional/defaulted so a plan doc written before bundles existed still
  // parses. firestore.rules' validPlanBundles caps this the same way
  // (size() <= 50) without inspecting individual entries — the shape check
  // happens here, at the trust boundary.
  bundles: z.array(PlanBundleSchema).max(MAX_BUNDLES_PER_PLAN).default([]),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});

// ----- plan events (Oct 2026) -----
//
// Glenn, Oct 20: "batches of defense work could be associated with events that
// stipulate a place and time, so community leaders could use our system to
// drive participation". And: "an event could pertain to multiple bundles and
// plans in an area, though it would normally be for a particular bundle".
//
// So an event is its own top-level record, planEvents/{eventId}, that links to
// bundles. v1 allows exactly one link and the owner must own that plan;
// linking more is a UI change later, not a migration. `planIds` and
// `bundleKeys` ("planId/bundleId") mirror `links` so clients can query with
// array-contains. Events are world-readable: an event is an invitation. The
// bundle's defenses only show on the event page when its plan is shared.
//
// Sign-ups live in planEvents/{eventId}/rsvps/{sha256(lowercased email)},
// readable by the event owner only and written only by the rsvpToPlanEvent
// Cloud Function, which also keeps `goingCount` (sum of party sizes).
export const MAX_EVENT_LINKS = 10;
export const PLAN_EVENT_LINKS_V1 = 1;
export const MAX_EVENT_PARTY_SIZE = 10;
export const MAX_EVENT_RSVPS = 300;
export const DEFAULT_EVENT_TIME_ZONE = 'America/Los_Angeles';

export const PlanEventLinkSchema = z.object({
  planId: z.string(),
  bundleId: z.string(),
});
export const planEventStatusSchema = z.enum(['scheduled', 'cancelled']);
export type PlanEventStatus = z.infer<typeof planEventStatusSchema>;

export const PlanEventMeetingPlaceSchema = z.object({
  label: z.string().min(1).max(120),
  lat: z.number().min(-90).max(90).optional(),
  lng: z.number().min(-180).max(180).optional(),
});

export const PlanEventSchema = z.object({
  id: z.string(),
  ownerUid: z.string(),
  links: z.array(PlanEventLinkSchema).min(1).max(MAX_EVENT_LINKS),
  planIds: z.array(z.string()).max(MAX_EVENT_LINKS),
  bundleKeys: z.array(z.string()).max(MAX_EVENT_LINKS),
  title: z.string().min(1).max(80),
  startsAt: TimestampSchema,
  endsAt: TimestampSchema,
  timeZone: z.string().min(1).max(64).default(DEFAULT_EVENT_TIME_ZONE),
  meetingPlace: PlanEventMeetingPlaceSchema,
  whatToBring: z.string().max(300).optional(),
  notes: z.string().max(600).optional(),
  status: planEventStatusSchema,
  goingCount: z.number().int().min(0).default(0),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});

export const PlanEventRsvpSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(80),
  email: z.string().email().max(254),
  partySize: z.number().int().min(1).max(MAX_EVENT_PARTY_SIZE),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});

// ----- defense plan "who's responsible" (Sept 2026, many-to-many revision) -----
//
// A responsible party is its own record, reusable across a plan's defenses
// (a defense can have several parties; a party can cover several defenses):
//
// - defensePlans/{planId}/parties/{partyId} — a general description of who:
//   a type, an organization and/or a person, and optionally the REDI user
//   when it's a self-claim.
// - defensePlans/{planId}/partyContacts/{partyId} — owner-private email,
//   kept out of the public party doc entirely. No email is sent in this
//   phase; a later Cloud Function reads this via the admin SDK.
// - defensePlans/{planId}/assignments/{featureId}__{partyId} — the link
//   between one defense and one party, with a status and a short
//   description of what that party will do. The doc id is deterministic so
//   a (feature, party) pair has at most one assignment.
//
// Labels for `type` live in src/components/map/planParties.ts (display
// only, easy for Glenn to edit); this enum is the validated source of
// truth.
export const planPartyTypeSchema = z.enum([
  'homeowner',
  'hoa',
  'unincorporated',
  'city',
  'county',
  'state',
  'national',
  'other',
  // Oct 2026: a contractor put on a bundle team by accepting their bid.
  'vendor',
]);

export const PlanPartySchema = z
  .object({
    id: z.string(),
    type: planPartyTypeSchema,
    organization: z.string().max(80).optional(),
    person: z.string().max(80).optional(),
    // The REDI user, when this party is a volunteer's own self-claim.
    // Absent for an owner-named party with no account of its own.
    uid: z.string().optional(),
    createdBy: z.string(),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
  })
  .refine((party) => !!(party.organization?.length || party.person?.length), {
    message: 'A party needs at least an organization or a person.',
    path: ['organization'],
  });

// Owner-private. Never merged into PlanPartySchema — a client that only
// has read access to `parties` must never see this shape at all.
export const PlanPartyContactSchema = z.object({
  id: z.string(),
  email: z.string().email().max(254).optional(),
  updatedAt: TimestampSchema,
});

export const planAssignmentStatusSchema = z.enum(['asked', 'accepted', 'done', 'declined']);

// ----- vendor bids on a bundle (Oct 2026, Glenn's CFSC vendor idea) -----
//
// defensePlans/{planId}/bids/{sha256(bundleId + '\n' + email)}: a vendor's
// "I can help" on a bundle the owner opened for bids. Written only by the
// submitBid Cloud Function (no account, like event sign-ups). The owner reads
// them and moves one to accepted or declined; nothing else is client-writable.
export const MAX_BIDS_PER_BUNDLE = 100;
export const MAX_BID_AMOUNT = 10_000_000;
export const planBidStatusSchema = z.enum(['submitted', 'accepted', 'declined']);
export const PlanBidSchema = z.object({
  id: z.string(),
  bundleId: z.string(),
  bundleName: z.string().max(60),
  organization: z.string().min(1).max(80),
  person: z.string().min(1).max(80),
  email: z.string().email().max(254),
  phone: z.string().max(40).optional(),
  /** Whole dollars. Absent when the vendor gave no price. */
  amount: z.number().int().min(0).max(MAX_BID_AMOUNT).optional(),
  note: z.string().max(500).optional(),
  status: planBidStatusSchema,
  /** The vendor party Accept created, so the row can say "On the team". */
  acceptedPartyId: z.string().optional(),
  /** The directory vendor this accepted bid came from, set once by the
   *  owner. It is what lets the owner rate that vendor. */
  vendorId: z.string().optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});

// ----- Redi bucks (simulated money, Oct 2026) -----
//
// A ledger, not a wallet: funds/{id} holds the pot and two server-kept
// counters, funds/{id}/ledger/{entryId} is the append-only history, and
// fundingRequests/{planId__bundleId__bidId} is where each request sits in the
// pipeline. Moderators record funds; only the requestFunding and
// reviewFundingRequest Cloud Functions move anything. See docs/redi-bucks.md.
export const MAX_FUND_AMOUNT = 100_000_000;
export const FundSchema = z.object({
  id: z.string(),
  areaId: z.string(),
  name: z.string().min(1).max(80),
  source: z.string().max(160).optional(),
  note: z.string().max(500).optional(),
  unit: z.literal('RB'),
  simulated: z.literal(true),
  amount: z.number().int().min(0).max(MAX_FUND_AMOUNT),
  committed: z.number().int().min(0),
  paid: z.number().int().min(0),
  createdBy: z.string(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});

export const fundLedgerKindSchema = z.enum(['commit', 'release', 'reverse']);
export const FundLedgerEntrySchema = z.object({
  id: z.string(),
  kind: fundLedgerKindSchema,
  amount: z.number().int().min(0),
  requestId: z.string(),
  planId: z.string(),
  planTitle: z.string().max(120),
  bundleId: z.string(),
  bundleName: z.string().max(60),
  partyId: z.string(),
  vendorName: z.string().max(80),
  byUid: z.string(),
  createdAt: TimestampSchema,
});

export const fundingRequestStatusSchema = z.enum([
  'requested',
  'committed',
  'paid',
  'declined',
  'withdrawn',
  'cancelled',
]);
export const FundingRequestSchema = z.object({
  id: z.string(),
  areaId: z.string(),
  planId: z.string(),
  planTitle: z.string().max(120),
  ownerId: z.string(),
  bundleId: z.string(),
  bundleName: z.string().max(60),
  bidId: z.string(),
  partyId: z.string(),
  /** The bid's organization. Never an email or a phone. */
  vendorName: z.string().max(80),
  amount: z.number().int().min(1).max(MAX_BID_AMOUNT),
  status: fundingRequestStatusSchema,
  requestedAt: TimestampSchema,
  requestedBy: z.string(),
  decidedAt: TimestampSchema.optional(),
  decidedBy: z.string().optional(),
  decisionNote: z.string().max(160).optional(),
  fundId: z.string().optional(),
  fundName: z.string().max(80).optional(),
  paidAt: TimestampSchema.optional(),
  paidBy: z.string().optional(),
  updatedAt: TimestampSchema,
});

// 'owner' — the plan owner linked this party. 'volunteer' — a signed-in
// homeowner linked their own party to a defense themselves.
export const planAssignmentSourceSchema = z.enum(['owner', 'volunteer']);

export const DefensePlanAssignmentSchema = z.object({
  id: z.string(),
  featureId: z.string(),
  partyId: z.string(),
  // A very general description of what this party will do on this defense,
  // e.g. "Clear brush along the ridge road".
  description: z.string().max(200).optional(),
  status: planAssignmentStatusSchema,
  source: planAssignmentSourceSchema,
  createdBy: z.string(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
  // Server-written only (Sept 2026 update loop —
  // functions/src/plans/requestPlanUpdate.ts and planUpdateLink.ts's
  // submitPlanUpdate). lastAskedAt is set every time the owner asks this
  // assignment's party for progress; lastUpdateAt/lastUpdateNote mirror the
  // most recent assignmentUpdates entry for quick display without a join —
  // the note is truncated to 120 chars for this denormalized copy, the full
  // note lives on the assignmentUpdates doc itself (PlanAssignmentUpdateSchema
  // below).
  lastAskedAt: TimestampSchema.optional(),
  lastUpdateAt: TimestampSchema.optional(),
  lastUpdateNote: z.string().max(120).optional(),
});

// ----- defense plan assignment updates (Sept 2026 update loop) -----
//
// defensePlans/{planId}/assignmentUpdates/{updateId} — one reported status
// change on one assignment, written by planUpdateLink.ts's submitPlanUpdate
// (source 'party-link', from the emailed link a leader with no account
// uses) or, in future, an owner/volunteer reporting progress directly
// (source 'owner'|'volunteer'). Read-only to every client — rules allow a
// read wherever the plan itself is readable and deny every client write
// (`allow write: if false`); only the Cloud Functions above ever write
// here, via the admin SDK. `newsPostId` is set once by
// publishPlanUpdateNews.ts when this update is rolled into a News post, and
// is what "unpublished" means — see defensePlansStore's
// unpublishedAssignmentUpdates selector.
export const planAssignmentUpdateStatusSchema = z.enum(['accepted', 'done', 'declined']);
export const planAssignmentUpdateSourceSchema = z.enum(['party-link', 'owner', 'volunteer']);

export const PlanAssignmentUpdateSchema = z.object({
  id: z.string(),
  assignmentId: z.string(),
  featureId: z.string(),
  partyId: z.string(),
  status: planAssignmentUpdateStatusSchema,
  // Up to 500 chars server-side (planUpdateLink.ts's MAX_NOTE_LENGTH) — the
  // full note a leader left, distinct from the assignment's own truncated
  // lastUpdateNote copy above.
  note: z.string().max(500).optional(),
  source: planAssignmentUpdateSourceSchema,
  // Only set for a party-link submission — the token that authored it.
  tokenId: z.string().optional(),
  createdAt: TimestampSchema,
  newsPostId: z.string().optional(),
});

// ----- map overlays (moderator-imported plan map images, Sept 2026) -----
//
// A moderator drops in an image of a page (or clipped section) of a
// community wildfire plan PDF and pins it to the Leaflet map by three
// corners — the fourth is implied, the same affine-rectangle convention
// Leaflet's distortable-image overlays use. 'shared' overlays are
// world-readable toggleable layers (the map is visible signed out, same
// reasoning as defensePlans' shared branch); 'draft' is visible only to
// moderators while still being placed. One doc per overlay
// (mapOverlays/{id}); the image itself lives at Storage path
// overlays/{id}/image.jpg.

export const mapOverlaySourceSchema = z.object({
  document: z.string(),
  page: z.number().nullable().optional(),
  label: z.string(),
  url: z.string().optional(),
});

export const mapOverlayCornerSchema = z.object({
  lat: z.number(),
  lng: z.number(),
});

export const mapOverlayCornersSchema = z.object({
  topLeft: mapOverlayCornerSchema,
  topRight: mapOverlayCornerSchema,
  bottomLeft: mapOverlayCornerSchema,
});

export const mapOverlayVisibilitySchema = z.enum(['draft', 'shared']);

export const MapOverlaySchema = z.object({
  id: z.string(),
  title: z.string().min(1).max(80),
  source: mapOverlaySourceSchema,
  areaId: z.string(),
  // Storage path (overlays/{id}/image.jpg) and its resolved download URL,
  // plus the source image's pixel dimensions — needed to keep the overlay's
  // aspect ratio correct when the placement editor lets a moderator nudge
  // one corner at a time.
  imagePath: z.string(),
  imageUrl: z.string(),
  imageWidth: z.number(),
  imageHeight: z.number(),
  corners: mapOverlayCornersSchema,
  opacity: z.number().min(0).max(1).default(0.6),
  visibility: mapOverlayVisibilitySchema,
  note: z.string().max(200).optional(),
  createdBy: z.string(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});

// ----- funding sources (grant/discount directory, Sept 2026) -----
//
// A read-only, seeded directory of programs that put money (or a premium
// discount) toward wildfire mitigation in a community — Glenn's "show a
// pool of money available" ask. Version one has no applications and no
// accounts; a moderator seeds and edits entries by hand (scripts/
// seed-funding-sources.ts). `areaIds` holds either specific service-area
// ids or `['*']` for a national/statewide program. `sourceCheckedAt` is
// when a human last verified the doc against the program's own page — a
// grant deadline goes stale, and the screen says when it was last looked at.

export const fundingSourceKindSchema = z.enum(['grant', 'discount']);

export const fundingSourceStatusSchema = z.enum(['open', 'closed', 'rolling', 'unannounced']);

export const FundingSourceSchema = z.object({
  id: z.string(),
  areaIds: z.array(z.string()).min(1),
  name: z.string().min(1).max(200),
  funder: z.string().min(1).max(200),
  summary: z.string().max(300),
  kind: fundingSourceKindSchema,
  // The program's ceiling in dollars, or null when no single number applies
  // (e.g. a total pool split across awards decided after applications close).
  maxAward: z.number().nullable(),
  awardNote: z.string().max(1000).nullable(),
  status: fundingSourceStatusSchema,
  deadline: TimestampSchema.nullable(),
  deadlineNote: z.string().max(500).nullable(),
  whoApplies: z.string().max(300),
  url: z.string(),
  sourceCheckedAt: TimestampSchema,
  published: z.boolean(),
  updatedAt: TimestampSchema,
});

// ----- area contacts (people directory, Sept 2026) -----
//
// Glenn's ask: a list of "people" (players in the area) — name, role,
// organization, phone, email — for a moderator to keep. It first fed the
// assign form's "add someone new" fields; since Oct 2026 Assign picks from
// the owner's own People lists instead, and moderators open this directory
// from the People panel. Unlike fundingSources this has no public
// read branch at all: these are real people's phone numbers and email
// addresses, not a seeded public directory. Moderators add/edit/delete by
// hand in the app; see src/api/areaContacts.ts and
// AreaPeopleView.web.tsx. `areaIds` follows fundingSources' convention —
// specific service-area ids, or `['*']` for everywhere.
//
// `partyType` reuses planPartyTypeSchema (defined above) rather than a
// second enum — same jurisdiction-level list Glenn already reviewed for
// plan parties. `role` is separate free text ("Battalion Chief") since a
// person's job title isn't one of those levels.
//
// Optional fields follow PlanPartySchema/PlanPartyContactSchema's
// convention: absent when unset (never null), same as the rest of this
// file's party types.
export const AreaContactSchema = z.object({
  id: z.string(),
  areaIds: z.array(z.string()).min(1),
  name: z.string().min(1).max(120),
  organization: z.string().max(200).optional(),
  role: z.string().max(100).optional(),
  partyType: planPartyTypeSchema,
  phone: z.string().max(40).optional(),
  // Same shape as PlanPartyContactSchema's email — zod's built-in
  // validator client-side; firestore.rules' validAreaContactFields() does
  // its own basic regex check server-side (real validation is out of
  // scope for either layer here).
  email: z.string().email().max(254).optional(),
  notes: z.string().max(500).optional(),
  createdBy: z.string(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});

// ----- peopleLists (uploaded leader spreadsheets, Oct 2026) -----
// Owner-only. `areaIds` is the area id at upload or ['*'] for everywhere.
// Optional fields are absent when empty (never null), like the party types.
export const PeopleListSchema = z.object({
  id: z.string(),
  ownerUid: z.string(),
  name: z.string().min(1).max(80),
  areaIds: z.array(z.string()).min(1).max(5),
  rowCount: z.number().int().min(0),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});

export const PersonSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(120),
  firstName: z.string().max(120).optional(),
  lastName: z.string().max(120).optional(),
  category: z.string().max(60).optional(),
  organization: z.string().max(120).optional(),
  suborganization: z.string().max(120).optional(),
  title: z.string().max(120).optional(),
  // Plain text only. Emails from an uploaded list are never used to send
  // anything and never copied into a plan's partyContacts.
  emails: z.array(z.string().max(254)).max(2),
  phone: z.string().max(40).optional(),
  profileUrl: z.string().max(300).optional(),
  note: z.string().max(1000).optional(),
  team: z.boolean(),
  sourceRow: z.number().int(),
});

// ----- vendors, vendorReps (shared vendor directory, Nov 2026) -----
// Glenn's Nov 2 sheet and notes: one shared, crowdsourced list of vendors
// for a region, which owners pick from when they invite vendors to bid on a
// bundle. A vendor is a company; each person at it is a rep, so a national
// firm can carry a local rep per region. Any signed-in account can suggest
// either one, and it stays 'suggested' (seen only by its author and
// moderators) until a moderator approves it. Moderator uploads write
// 'approved' docs directly. Reps are top-level, keyed back by vendorId, so
// the whole directory loads in two queries.
//
// `categories` are labels, not ids, so a new kind of work from the crowd
// doesn't need a deploy; src/constants/vendorCategories.ts maps the known
// ones to defense strategies. `areaIds` follows fundingSources' convention:
// service-area ids, or ['*'] for a vendor who works everywhere. Rep emails
// and phones are for signed-in readers only and are never copied into a
// plan (see invitedVendors above).
export const vendorStatusSchema = z.enum(['suggested', 'approved', 'rejected']);
// 'ai': found on the web by the moderator's vendor search (Nov 2026), with
// the page it came from in sourceUrl. Only vendors, never reps, carry it.
export const vendorSourceSchema = z.enum(['upload', 'user', 'ai']);
export const MAX_VENDOR_CATEGORIES = 12;
export const MAX_VENDOR_AREAS = 10;

export const VendorSchema = z.object({
  id: z.string(),
  organization: z.string().min(1).max(200),
  // Lowercased, punctuation and company suffixes stripped (vendorNameKey in
  // src/utils/vendors.ts). An upload matches existing vendors on it.
  nameKey: z.string().min(1).max(200),
  website: z.string().max(300).optional(),
  domain: z.string().max(200).optional(),
  categories: z.array(z.string().min(1).max(60)).max(MAX_VENDOR_CATEGORIES),
  areaIds: z.array(z.string()).min(1).max(MAX_VENDOR_AREAS),
  // The sheet's own words ("San Bernadino", "National"), kept for review.
  serviceAreaText: z.string().max(200).optional(),
  licenses: z.string().max(300).optional(),
  cfscListed: z.boolean().optional(),
  notes: z.string().max(1000).optional(),
  status: vendorStatusSchema,
  source: vendorSourceSchema,
  sourceUrl: z.string().max(500).optional(),
  createdBy: z.string(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
  reviewedBy: z.string().optional(),
  reviewedAt: TimestampSchema.optional(),
});

export const VendorRepSchema = z.object({
  id: z.string(),
  vendorId: z.string(),
  name: z.string().min(1).max(120),
  role: z.string().max(100).optional(),
  email: z.string().max(254).optional(),
  phone: z.string().max(40).optional(),
  areaIds: z.array(z.string()).min(1).max(MAX_VENDOR_AREAS),
  status: vendorStatusSchema,
  source: vendorSourceSchema,
  createdBy: z.string(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
  reviewedBy: z.string().optional(),
  reviewedAt: TimestampSchema.optional(),
});

// A fix proposed to an approved vendor or rep (Nov 2026). `changes` holds
// only the fields that change; '' clears an optional string. A moderator's
// approval applies it to the vendor (or the rep, when repId is set).
export const VendorEditChangesSchema = z.object({
  organization: z.string().min(1).max(200).optional(),
  website: z.string().max(300).optional(),
  categories: z.array(z.string().min(1).max(60)).max(MAX_VENDOR_CATEGORIES).optional(),
  areaIds: z.array(z.string()).min(1).max(MAX_VENDOR_AREAS).optional(),
  serviceAreaText: z.string().max(200).optional(),
  licenses: z.string().max(300).optional(),
  notes: z.string().max(1000).optional(),
});
export const RepEditChangesSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  role: z.string().max(100).optional(),
  email: z.string().max(254).optional(),
  phone: z.string().max(40).optional(),
});
export const MAX_VENDOR_EDIT_NOTE = 300;

export const VendorEditSchema = z.object({
  id: z.string(),
  vendorId: z.string(),
  repId: z.string().optional(),
  // One object, not a union: zod strips unknown keys, so a union's first
  // branch would quietly eat a rep's fields. Rules keep the two apart.
  changes: VendorEditChangesSchema.merge(RepEditChangesSchema),
  note: z.string().max(MAX_VENDOR_EDIT_NOTE).optional(),
  status: vendorStatusSchema,
  createdBy: z.string(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
  reviewedBy: z.string().optional(),
  reviewedAt: TimestampSchema.optional(),
});

// One rating per account per vendor, at vendorRatings/{vendorId}__{uid}
// (Nov 2026). Only an owner whose plan accepted that vendor's bid may rate:
// planId and bidId name that bid, and rules check it. A moderator hides a
// rating instead of deleting it.
export const MAX_VENDOR_RATING_NOTE = 280;
export const VendorRatingSchema = z.object({
  id: z.string(),
  vendorId: z.string(),
  createdBy: z.string(),
  stars: z.number().int().min(1).max(5),
  note: z.string().max(MAX_VENDOR_RATING_NOTE).optional(),
  planId: z.string(),
  bidId: z.string(),
  hidden: z.boolean(),
  hiddenBy: z.string().optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});

// ----- notification preferences (embedded in user doc) -----

export const NotificationPreferencesSchema = z.object({
  newPosts: z.boolean(),
  comments: z.boolean(),
  likes: z.boolean(),
  checklistReminders: z.boolean(),
  communityUpdates: z.boolean(),
});

// ----- users -----

export const AgentProfileSchema = z.object({
  insurerName: z.string(),
  inviteCode: z.string().optional(),
  grantedAt: TimestampSchema,
  // Server-controlled verification gate. Written only by the
  // provisionAgentProfile / verifyAgent Cloud Functions (admin SDK).
  // Insurance privilege (reading 'insurance'-visibility posts, creating
  // insurer checklists, generating reports as an agent) requires
  // status === 'verified'. Self-service signup creates 'pending', which
  // unlocks nothing until an admin verifies the agent. Default 'pending'
  // so any profile missing the field is treated as unverified.
  status: z.enum(['pending', 'verified']).default('pending'),
});

export const UserSchema = z.object({
  id: z.string(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  emailNotificationsEnabled: z.boolean(),
  notificationPreferences: NotificationPreferencesSchema.optional(),
  postsCount: z.number(),
  avatarUrl: z.string().optional(),
  avatarPath: z.string().optional(),
  agentProfile: AgentProfileSchema.optional(),
  hasCompletedOnboarding: z.boolean().optional(),
  // Multi-role: every user is at minimum a Fire Scout. Optional during
  // migration; reads should treat missing/empty as ['fire-scout'].
  roles: z.array(userRoleSchema).optional(),
  // Anonymous (guest) Firebase Auth users get roles:['fire-scout'] and
  // isAnonymous:true. Posts they create are attributed to "Fire Scout"
  // (anonymous by default).
  isAnonymous: z.boolean().optional(),
  stakeholderType: stakeholderTypeSchema.nullable().optional(),
  isInServiceArea: z.boolean().nullable().optional(),
  serviceAreaName: z.string().nullable().optional(),
  invitedByAgentId: z.string().optional(),
  defaultInsurer: z.string().optional(),
  // Denormalized unread-notifications badge counter: incremented by the
  // sendNotification function, decremented/zeroed by markAsRead/markAllAsRead.
  // The badge listens to this doc instead of streaming the unread set.
  unreadCount: z.number().optional(),
  // Denormalized list of insurance agents linked to this account, kept in
  // step by redeemInviteCode (and scripts/link-demo-agent.ts) so a client
  // can render "your agent(s)" without a join. Server-written only — see
  // the frozen-fields list in the users update rule in firestore.rules.
  linkedAgents: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        insurerName: z.string(),
      })
    )
    .optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});

// ----- structures (read-only seed data + denormalized claim summary) -----

export const ClaimSummarySchema = z.object({
  claimedBy: z.string().optional(),
  progressPercent: z.number().optional(),
});

// claimSummaries/{areaId} — function-maintained, per-area map of claim
// state for the pin layer (see functions/src/triggers/onStructureClaimWritten).
// The map subscribes to one of these per intersecting area instead of
// streaming every claim doc.
export const AreaClaimSummariesSchema = z.object({
  id: z.string(),
  structures: z
    .record(
      z.string(),
      z.object({
        claimedBy: z.string(),
        completed: z.number().optional(),
        total: z.number().optional(),
      })
    )
    .default({}),
  updatedAt: TimestampSchema.optional(),
});

// Origin of a structure record. Missing = 'seed' for back-compat with the
// already-shipped GeoJSON shards, where structures have no source field.
export const structureSourceSchema = z.enum(['seed', 'user-added']);

export const StructureSchema = z.object({
  id: z.string(),
  centroid: z.object({ latitude: z.number(), longitude: z.number() }),
  // Still stored on Firestore docs; dropped from the static GeoJSON shards
  // to cut payload. Not read by any client-side runtime code.
  geohash: z.string().optional(),
  geometry: GeoJSONPolygonSchema.optional(),
  address: z.string().optional(),
  city: z.string().default(''),
  state: z.string().default(''),
  zip: z.string().nullish(),
  propertyType: z.string().nullish(),
  postsCount: z.number().default(0),
  claimSummary: ClaimSummarySchema.nullable().optional(),
  // User-added structures get source:'user-added' and verified:false. Seed
  // structures shipped in GeoJSON have neither field; readers should treat
  // missing source as 'seed' and missing verified as true.
  source: structureSourceSchema.optional(),
  verified: z.boolean().optional(),
  addedBy: z.string().optional(),
  // Denormalized at create time. Lets the client filter live user-added
  // structures by area without re-deriving from the centroid.
  serviceAreaId: z.string().optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});

// ----- structure claims (user-generated, source of truth for claim state) -----

export const ProgressSnapshotSchema = z.object({
  completed: z.number(),
  total: z.number(),
  updatedAt: TimestampSchema,
});

export const StructureClaimSchema = z.object({
  id: z.string(),
  claimedBy: z.string(),
  claimedAt: TimestampSchema,
  // Denormalized from the structure's centroid → service area at claim time.
  // Used to scope the map's area-wide claims subscription so a client never
  // streams claims from other regions. Immutable after create (the structure
  // doesn't move, and a claim moving areas would be a different claim).
  serviceAreaId: z.string(),
  stakeholderType: stakeholderTypeSchema.optional(),
  insurerChecklistId: z.string().optional(),
  reportRecipients: z.array(z.string()).optional(),
  // Owner-set default visibility for posts attached to this claim's
  // structure. Individual posts may override via `visibility`. Missing =
  // 'public'.
  defaultPostVisibility: postVisibilitySchema.optional(),
  progressSnapshot: ProgressSnapshotSchema.optional(),
  // Report-send history, denormalized by buildAndSendReport (server-written
  // only — rules deny these keys in client claim updates). Optional for
  // back-compat with claims that predate the denormalization.
  lastReport: z
    .object({
      at: TimestampSchema,
      byRole: z.string(),
      recipientsCount: z.number(),
    })
    .optional(),
  reportCount: z.number().optional(),
  // "Notify my agent" state — server-written only (notifyAgent Cloud
  // Function). evaluationStatus is the owner's self-reported status as of
  // the last notify send; evaluationStatusAt is when it was set.
  evaluationStatus: evaluationStatusSchema.optional(),
  evaluationStatusAt: TimestampSchema.optional(),
  lastAgentNotification: z
    .object({
      at: TimestampSchema,
      status: z.string(),
      recipientsCount: z.number(),
    })
    .optional(),
  updatedAt: TimestampSchema,
});

// ----- posts + comments -----

export const PostSchema = z.object({
  id: z.string(),
  authorId: z.string(),
  authorName: z.string(),
  authorAvatarUrl: z.string().nullish(),
  isAnonymous: z.boolean(),
  category: postCategorySchema,
  // Optional since Aug 2026: news posts are text-first and may carry no
  // photo. Observations (progress/risk) still require one, enforced in the
  // composer — kept loose here so no legacy doc ever fails a read.
  imageUrl: z.string().optional(),
  imagePath: z.string().optional(),
  thumbnailUrl: z.string().optional(),
  thumbnailPath: z.string().optional(),
  // 1600x1600 WebP for the full-screen slideshow. Same async-fill pattern as
  // thumbnailUrl: the client predicts displayPath at create time, the
  // Resize Images extension generates the file, onImageResized fills
  // displayUrl once it lands.
  displayUrl: z.string().optional(),
  displayPath: z.string().optional(),
  caption: z.string(),
  // Optional headline, news posts only (Aug 2026). The News tab renders it
  // where the lede heuristic (utils/newsCaption.ts splitNewsCaption) would
  // otherwise guess one from the caption's first line. Observations never
  // carry it — the composer only offers the field in news mode.
  title: z.string().optional(),
  // Server-stored docs use null for "no location" (locationHidden) and an
  // object otherwise. Firestore reads return null literally, not undefined.
  location: LocationSchema.nullable(),
  // Legacy: hides GPS from a post. Client UI no longer offers the toggle
  // (per client plan: "do not allow hiding of location") but the field
  // stays readable so existing posts still validate.
  locationHidden: z.boolean(),
  structureId: z.string().optional(),
  // Owner-controlled visibility scope. Missing = 'public' for back-compat
  // with posts created before the field existed.
  visibility: postVisibilitySchema.optional(),
  // Street address captured at create time (reverse-geocoded). Lets posts
  // associate with a property even when no structure pin exists.
  address: z.string().optional(),
  // For Fire Scout progress posts attached to a checklist item without
  // marking it complete (owners/agents still control completion).
  checklistItemId: z.string().optional(),
  // Denormalized at create/edit time so cards can show which step the post
  // documents without fetching the checklist.
  checklistItemLabel: z.string().optional(),
  // Before/after pairing: an "after" post points at the earlier "before"
  // post it documents. One-directional — the before post is never touched
  // (posts updates are author-only). Image/date are snapshotted so feed
  // cards render the pair with zero extra reads.
  beforePostId: z.string().optional(),
  beforePostImageUrl: z.string().optional(),
  beforePostThumbnailUrl: z.string().optional(),
  beforePostCreatedAt: TimestampSchema.optional(),
  // Self-contained before/after pair: both images live on the same post
  // doc rather than pointing at an earlier post. beforeThumbnailUrl is
  // filled async by the Resize Images extension (forward hook only — the
  // client writes beforeThumbnailPath at create time).
  beforeImageUrl: z.string().optional(),
  beforeImagePath: z.string().optional(),
  beforeThumbnailPath: z.string().optional(),
  beforeThumbnailUrl: z.string().optional(),
  likesCount: z.number(),
  commentsCount: z.number(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema.optional(),
});

export const CommentSchema = z.object({
  id: z.string(),
  authorId: z.string(),
  authorName: z.string(),
  text: z.string(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema.optional(),
});

// ----- checklists -----

export const ChecklistItemSchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string().optional(),
  category: z.string().optional(),
  // Shared physical work this item documents, so an observation logged under
  // an equivalent item in another model still surfaces here. See
  // src/constants/checklistTopics.ts. Deliberately a plain string rather than
  // the ChecklistTopic union: a client on an older bundle must still parse a
  // checklist that introduced a new topic, or mapDocs would drop the whole doc
  // and the checklist screen would render empty.
  topic: z.string().optional(),
});

// A checklist doc is also a selectable "preparation model" (e.g. California
// FAIR Plan, Hurricane Home Hardening, REDI Baseline). `insurerName` stays for
// back-compat; `displayName` is the user-facing model name (falls back to
// `insurerName`); `hazard` groups/labels models by the risk they address; and
// `sponsor` is the org/insurer advocating the model (shown as "Advocated by
// {sponsor}" when it adds info beyond the model name).
export const hazardKindSchema = z.enum(['wildfire', 'storm', 'general']);

// A checklist can carry a co-brand for the insurer-link journey: the visitor
// who followed `/?property=<id>&model=<checklistId>` sees a strip naming the
// sponsoring insurer plus an accent color override, not a full theme swap
// (see src/theme/brand.ts). accentLight/accentDark must each clear 4.5:1
// against the matching theme's `background` token.
export const ChecklistBrandSchema = z.object({
  name: z.string(),
  accentLight: z.string(),
  accentDark: z.string(),
  logoUrl: z.string().optional(),
});

export const InsuranceChecklistSchema = z.object({
  id: z.string(),
  insurerName: z.string(),
  displayName: z.string().optional(),
  hazard: hazardKindSchema.optional(),
  sponsor: z.string().optional(),
  stakeholderType: stakeholderTypeSchema,
  serviceAreaIds: z.array(z.string()).optional(),
  isDefault: z.boolean().optional(),
  // Pre-selected in the claim picker for its stakeholder type (still
  // user-changeable there); distinct from `isDefault`, the silent fallback
  // for claims with no model assigned.
  isRecommended: z.boolean().optional(),
  // Still being checked against the published standard; the picker shows a
  // "Draft" label.
  draft: z.boolean().optional(),
  items: z.array(ChecklistItemSchema),
  reportThresholds: z.array(z.number()).optional(),
  brand: ChecklistBrandSchema.optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});

export const ChecklistThreadMessageSchema = z.object({
  id: z.string(),
  authorId: z.string(),
  authorName: z.string(),
  authorAvatarUrl: z.string().nullish(),
  authorRole: checklistThreadAuthorRoleSchema,
  text: z.string(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema.optional(),
});

export const CompletedChecklistItemSchema = z.object({
  itemId: z.string(),
  completedAt: TimestampSchema,
  photoUrl: z.string().optional(),
  photoPath: z.string().optional(),
  notes: z.string().optional(),
});

export const ChecklistProgressSchema = z.object({
  structureId: z.string(),
  checklistId: z.string(),
  completedItems: z.array(CompletedChecklistItemSchema),
  reportsSent: z.array(z.number()).optional(),
  updatedAt: TimestampSchema,
});

// ----- notifications (in-app activity feed) -----

export const notificationTypeSchema = z.enum([
  'newPost',
  'comment',
  'like',
  'checklistReminder',
  'communityUpdate',
]);

export const NotificationSchema = z.object({
  id: z.string(),
  recipientId: z.string(),
  type: notificationTypeSchema,
  title: z.string(),
  body: z.string(),
  data: z.object({
    structureId: z.string().optional(),
    postId: z.string().optional(),
    checklistId: z.string().optional(),
  }),
  read: z.boolean(),
  createdAt: TimestampSchema,
});

// ----- user devices (FCM tokens) -----

export const DeviceSchema = z.object({
  id: z.string(),
  token: z.string(),
  platform: z.enum(['web', 'ios', 'android']),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});

// ----- service areas -----

export const ServiceAreaSchema = z.object({
  id: z.string(),
  name: z.string(),
  active: z.boolean(),
  bounds: z.object({
    minLat: z.number(),
    maxLat: z.number(),
    minLng: z.number(),
    maxLng: z.number(),
  }),
  center: z
    .object({
      latitude: z.number(),
      longitude: z.number(),
      zoom: z.number(),
    })
    .optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema.optional(),
});
